import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';

interface InlineEditFormProps {
  initialContent: string;
  onSave: (content: string) => Promise<void>;
  onCancel: () => void;
}

const InlineEditForm: React.FC<InlineEditFormProps> = ({
  initialContent,
  onSave,
  onCancel,
}) => {
  const [content, setContent] = useState(initialContent);
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(content.length, content.length);
    }
  }, []);

  const handleSave = async () => {
    const trimmed = content.trim();
    if (!trimmed || trimmed === initialContent) {
      onCancel();
      return;
    }
    setIsSaving(true);
    try {
      await onSave(trimmed);
    } catch {
      // keep form open so the user can retry
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <textarea
        ref={textareaRef}
        value={content}
        onChange={e => setContent(e.target.value)}
        className="w-full text-sm border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y min-h-[60px]"
        disabled={isSaving}
      />
      <div className="flex items-center gap-2">
        <Button
          onClick={handleSave}
          disabled={isSaving || !content.trim()}
          size="sm"
          className="text-xs"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
        <Button
          onClick={onCancel}
          disabled={isSaving}
          size="sm"
          variant="outline"
          className="text-xs"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default InlineEditForm;
